import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { makeApiRequest } from '../../lib/http/makeApiRequest';
import { Plan, PlansResponse, ProxyAddons } from '../../types/pricing.types';
import { createPaymentSession } from './api';
import { useAtom } from 'jotai';
import { user } from '../../state/atoms/user.atom';
import { PricingCard } from './pricing-card';
import { CustomPricingCard } from './custom-pricing-card';
import { BillingSwitch } from './billing-switch';
import { isPlanDowngradeDisabled } from './is-plan-downgrade-disabled';
import { ActivePlanSubscription } from './plan-downgrade.types';

export const PricingPage = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<'month' | 'year'>('year');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [proxyAddons, setProxyAddons] = useState<ProxyAddons>({ priceForGb: 0, priceForGbYearly: 0 });
  const [proxyTrafficByPlan, setProxyTrafficByPlan] = useState<Record<string, number>>({});
  const [selectedCustomPlanId, setSelectedCustomPlanId] = useState<string>('');
  const location = useLocation();
  const [hasHitLimit, setHasHitLimit] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'fail' | null>(null);
  const navigate = useNavigate();
  const [userData] = useAtom(user);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const limitParam = searchParams.get('limit') === 'true';
    const paymentStatusParam = searchParams.get('paymentStatus');

    if (limitParam) {
      setHasHitLimit(true);
    }

    if (paymentStatusParam === 'success' || paymentStatusParam === 'fail') {
      setPaymentStatus(paymentStatusParam);
    }

    const fetchPlans = async () => {
      const response: PlansResponse = await makeApiRequest('/v1/plans');
      setPlans(response.plans || []);
      setProxyAddons({
        priceForGb: response.proxyAddons?.priceForGb || 0,
        priceForGbYearly: response.proxyAddons?.priceForGbYearly || 0,
      });

      const customFromResponse = response.plans?.filter((p) => p.planType === 'custom') || [];
      const userCustomPlan = customFromResponse.find((p) => p.id === userData?.limits?.planId);
      const defaultCustom = userCustomPlan ?? customFromResponse[0];

      if (defaultCustom) {
        setSelectedCustomPlanId(defaultCustom.id);
      }
    };

    fetchPlans();
  }, [location.search, userData?.limits?.planId]);

  const regularPlans = plans.filter((p) => p.planType !== 'custom');
  const customPlans = plans.filter((p) => p.planType === 'custom');

  const handleProxyTrafficChange = (planId: string, amount: number) => {
    setProxyTrafficByPlan((prev) => ({ ...prev, [planId]: amount }));
  };

  const calculateMonthPrice = (basePrice: number) => {
    if (period === 'year') {
      return Math.round(basePrice / 12 / 100);
    }

    return basePrice / 100;
  };

  const proxyPricePerGbMonthly = period === 'year'
    ? (proxyAddons.priceForGbYearly || 0) / 12
    : proxyAddons.priceForGb;

  const proxyAddonTotalPerGb = period === 'year'
    ? proxyAddons.priceForGbYearly
    : proxyAddons.priceForGb;

  const calculateFinalPrice = (plan: Plan): number => {
    const basePrice = calculateMonthPrice(period === 'year' ? plan.priceForYear : plan.priceForMonth);
    const planProxyTraffic = proxyTrafficByPlan[plan.id] || 0;
    const addonsPrice = planProxyTraffic > 0 ? planProxyTraffic * proxyPricePerGbMonthly : 0;

    return basePrice + addonsPrice;
  };

  const handleSelectPlan = async (planId: string) => {
    const planProxyTraffic = proxyTrafficByPlan[planId] || 0;
    const addons = planProxyTraffic > 0
      ? [{ proxyType: 'resident', gbAmount: planProxyTraffic }]
      : [];

    await createPaymentSession({
      period,
      plan: planId,
      addons,
    }).catch((error) => {
      console.error('Error creating payment session:', error);
      navigate('/signin');
    });
  };

  const selectedCustomPlan = customPlans.find((p) => p.id === selectedCustomPlanId) ?? customPlans[0];
  const userPlanId = userData?.limits?.planId;
  const userPlanPeriod = userData?.planPeriod;

  let activeSubscription: ActivePlanSubscription | null = null;
  if (
    userData
    && userData.planName !== 'Free'
    && userPlanId
    && (userPlanPeriod === 'month' || userPlanPeriod === 'year')
  ) {
    activeSubscription = {
      planId: userPlanId,
      profiles: userData.limits.activeProfiles,
      period: userPlanPeriod,
    };
  }

  const isSelectedCustomUserPlan = !!selectedCustomPlan
    && selectedCustomPlan.id === userPlanId
    && userPlanPeriod === period;

  const handlePaymentModalClose = () => {
    setPaymentStatus(null);
    navigate('/pricing', { replace: true });
  };

  return (
    <div className='px-4 pb-10 pt-8 md:px-8 md:pt-12'>
      <header className='text-center'>
        {hasHitLimit ? (
          <>
            <div className='mb-4 flex justify-center'>
              <AlertTriangle className='h-12 w-12 text-ob-warning' />
            </div>
            <h1 className='text-3xl font-semibold leading-[1.15] tracking-[-0.02em] text-ob-text [text-wrap:balance] md:text-[38px]'>
              You've reached your limit
            </h1>
            <p className='mx-auto mt-3 max-w-2xl text-base text-ob-text-subtle md:text-lg'>
              You cannot perform this action because you've reached your current plan's limit.
              Please upgrade to a paid plan to continue using this feature.
            </p>
            <div className='mx-auto mt-6 max-w-xl rounded-[10px] border border-ob-warning/40 bg-ob-warning/10 p-4 text-ob-text-secondary'>
              Upgrading your plan will give you access to more profiles, better proxy options,
              and enhanced browser speed.
            </div>
          </>
        ) : (
          <>
            <h1 className='text-3xl font-semibold leading-[1.15] tracking-[-0.02em] text-ob-text [text-wrap:balance] md:text-[38px]'>
              {t('pricing.title')}
            </h1>
            <p className='mt-3 text-base text-ob-text-subtle md:text-lg'>
              {t('pricing.subtitle.text')}
            </p>
          </>
        )}
      </header>

      <div className='mt-7 flex flex-col items-center gap-2.5'>
        <div className='flex items-center gap-3 font-medium'>
          <label htmlFor='billing-period' className='cursor-pointer'>Monthly billing</label>
          <BillingSwitch
            id='billing-period'
            checked={period === 'year'}
            onCheckedChange={(checked) => setPeriod(checked ? 'year' : 'month')}
          />
          <label htmlFor='billing-period' className='cursor-pointer'>Annual billing</label>
        </div>
        <span className='whitespace-nowrap rounded-[14px] bg-ob-raised px-3 py-1.5 text-sm text-ob-text-muted'>
          Save 30% with annual billing
        </span>
      </div>

      <section
        aria-label='Pricing plans'
        className='mx-auto mt-[34px] grid max-w-[620px] grid-cols-1 items-stretch gap-5 xl:max-w-[1240px] xl:grid-cols-3'
      >
        {regularPlans.map((plan) => {
          const basePrice = calculateMonthPrice(period === 'year' ? plan.priceForYear : plan.priceForMonth);
          const finalPrice = calculateFinalPrice(plan);
          const isFreePlan = plan.priceForMonth === 0;
          const isUserPlan = plan.id === userPlanId && (isFreePlan || userPlanPeriod === period);
          const isDowngradeDisabled = !isFreePlan
            && isPlanDowngradeDisabled({
              selectedPlanProfiles: plan.profiles,
              selectedPeriod: period,
              activeSubscription,
            });

          return (
            <PricingCard
              key={plan.id}
              planId={plan.id}
              isUserPlan={isUserPlan}
              isDowngradeDisabled={isDowngradeDisabled}
              {...plan}
              features={plan.features || []}
              price={basePrice}
              period={period}
              priceForYear={plan.priceForYear / 100}
              isHighlighted={plan.isHighlighted}
              buttonText={hasHitLimit ? 'Upgrade Now' : t('pricing.planTableButton.professional')}
              onSelect={() => handleSelectPlan(plan.id)}
              isUserAuthorized={!!userData}
              addonPricePerGb={proxyAddonTotalPerGb}
              proxyTrafficGb={proxyTrafficByPlan[plan.id] || 0}
              onProxyTrafficChange={(amount) => handleProxyTrafficChange(plan.id, amount)}
              finalPrice={finalPrice}
            />
          );
        })}

        {customPlans.length > 0 && selectedCustomPlan && (
          <CustomPricingCard
            plans={customPlans}
            selectedPlanId={selectedCustomPlan.id}
            onChangePlan={setSelectedCustomPlanId}
            price={calculateMonthPrice(period === 'year' ? selectedCustomPlan.priceForYear : selectedCustomPlan.priceForMonth)}
            priceForYear={selectedCustomPlan.priceForYear / 100}
            finalPrice={calculateFinalPrice(selectedCustomPlan)}
            period={period}
            isUserPlan={isSelectedCustomUserPlan}
            isDowngradeDisabled={isPlanDowngradeDisabled({
              selectedPlanProfiles: selectedCustomPlan.profiles,
              selectedPeriod: period,
              activeSubscription,
            })}
            isUserAuthorized={!!userData}
            addonPricePerGb={proxyAddonTotalPerGb}
            proxyTrafficGb={proxyTrafficByPlan[selectedCustomPlan.id] || 0}
            onProxyTrafficChange={(amount) => handleProxyTrafficChange(selectedCustomPlan.id, amount)}
            buttonText={hasHitLimit ? 'Upgrade Now' : 'Try Custom'}
            onSelect={() => handleSelectPlan(selectedCustomPlan.id)}
          />
        )}
      </section>
      <Dialog
        open={paymentStatus !== null}
        onOpenChange={(open) => {
          if (!open) {
            handlePaymentModalClose();
          }
        }}
      >
        <DialogContent className='max-w-sm'>
          <div className='flex flex-col items-center gap-4 py-4'>
            {paymentStatus === 'success' ? (
              <>
                <CheckCircle className='h-16 w-16 text-ob-success' />
                <h2 className='text-xl font-semibold text-ob-text'>{t('Payment was successful')}</h2>
                <p className='text-center text-ob-text-muted'>{t('Thank you for your purchase. Your plan has been activated.')}</p>
              </>
            ) : null}
            {paymentStatus === 'fail' ? (
              <>
                <XCircle className='h-16 w-16 text-ob-danger' />
                <h2 className='text-xl font-semibold text-ob-text'>{t('Payment failed')}</h2>
                <p className='text-center text-ob-text-muted'>{t('Something went wrong with your payment. Please try again or contact support.')}</p>
              </>
            ) : null}
          </div>
          <DialogFooter className='sm:justify-center'>
            <Button variant='ob-primary' size='pill-sm' onClick={handlePaymentModalClose}>
              {t('Ok')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
